import { useTranslation } from 'react-i18next';
import DataTable, { type Column } from '@/components/base/DataTable';
import type { Driver } from '@/types';
import { AccountDisabledBadge, AvailabilityBadge } from './DriverStatusBadges';

interface DriversTableProps {
  drivers: Driver[];
  onView: (driver: Driver) => void;
}

function DriverCell({ driver }: { driver: Driver }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-500 text-sm font-bold text-background-50">
        {driver.avatarUrl ? (
          <img src={driver.avatarUrl} alt={driver.fullName} className="h-full w-full object-cover" />
        ) : (
          driver.fullName.charAt(0)
        )}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-foreground-950">{driver.fullName}</span>
        <span className="lb-ltr-field mt-0.5 block text-xs text-foreground-400">{driver.id}</span>
      </span>
    </div>
  );
}

function StatusCell({ driver }: { driver: Driver }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <AvailabilityBadge value={driver.availability} />
      {driver.accountDisabled ? <AccountDisabledBadge /> : null}
    </div>
  );
}

function ViewButton({ driver, onView }: { driver: Driver; onView: (driver: Driver) => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() => onView(driver)}
      className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
    >
      <i className="ri-eye-line text-base" aria-hidden="true" />
      {t('drivers.action.viewDetails')}
    </button>
  );
}

export default function DriversTable({ drivers, onView }: DriversTableProps) {
  const { t } = useTranslation();

  const columns: Column<Driver>[] = [
    { key: 'photo', header: t('drivers.table.photo'), render: (d) => <DriverCell driver={d} /> },
    { key: 'phone', header: t('drivers.table.phone'), render: (d) => <span className="lb-ltr-field text-sm text-foreground-800">{d.phone}</span> },
    { key: 'username', header: t('drivers.table.username'), render: (d) => <span className="lb-ltr-field text-sm text-foreground-800">@{d.username}</span> },
    { key: 'availability', header: t('drivers.table.availability'), render: (d) => <StatusCell driver={d} /> },
    {
      key: 'actions',
      header: t('drivers.table.actions'),
      className: 'text-end',
      render: (d) => (
        <div className="flex justify-end">
          <ViewButton driver={d} onView={onView} />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="hidden md:block">
        <DataTable columns={columns} rows={drivers} rowKey={(d) => d.id} />
      </div>

      <ul className="divide-y divide-background-100 md:hidden">
        {drivers.map((driver) => (
          <li key={driver.id} className="space-y-3 p-4">
            <div className="flex items-start justify-between gap-3">
              <DriverCell driver={driver} />
              <StatusCell driver={driver} />
            </div>
            <dl className="grid grid-cols-2 gap-2 rounded-md border border-background-200 bg-background-100/50 p-3 text-sm">
              <div>
                <dt className="text-xs text-foreground-500">{t('drivers.table.phone')}</dt>
                <dd className="lb-ltr-field mt-0.5 text-foreground-800">{driver.phone}</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">{t('drivers.table.username')}</dt>
                <dd className="lb-ltr-field mt-0.5 text-foreground-800">@{driver.username}</dd>
              </div>
            </dl>
            <div className="border-t border-background-100 pt-3">
              <ViewButton driver={driver} onView={onView} />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}